import React, { useEffect, useState, useCallback, useRef } from 'react';
import '@charitystack/shared/src/styles/frosted.css';
import { getBackendUrl, isDevelopment, logApiError } from './config/env';
import { BrandColorProvider, LEGACY_UNBRANDED_FORM_COLOR, buttonVariants, useBrandColors, OrgLogoTile, safePostMessageToParent, guardFocusWhileDocumentUnfocused, useDrawerTabHandoff } from '@charitystack/shared';
import { cn } from './lib/utils';
import OverlayDetailPane from './Components/OverlayDetailPane';
import EmbedForm from './EmbedForm';
import CampaignLeftPanel from './Components/CampaignLeftPanel';
import OverlaySkeleton from './Components/skeleton/OverlaySkeleton';
import { trackForm, FORM_EVENTS } from '@charitystack/shared';
import { OVERLAY_CARD_MIN_HEIGHT, OVERLAY_CARD_STANDARD_MIN_HEIGHT } from './Util/formHeightUtils';
import * as Drawer from 'vaul';
import { buildGroupTicketV2ElementsConfigUrl } from './Util/groupTicketContract';
import {
  OVERLAY_OPENER_ATTRIBUTE,
  scheduleFocusReturnAfterCheckout,
} from './Util/checkoutFocusReturn';
import { escapeBelongsToOpenPopup } from './Util/overlayEscape';
import { FollowDisplayCurrency } from './currency/DisplayCurrencyProvider';

/**
 * OverlayElement Component
 *
 * Two-pane overlay that composes OverlayDetailPane (left/top) + EmbedForm (right/bottom).
 * Triggered by ?donate&formid=abc (fundraisers) or ?attend&formid=abc (events) on nonprofit sites.
 *
 * Desktop: Side-by-side layout with detail pane (left) and form (right)
 * Mobile: Detail on top, form below in a scrollable view
 *
 * Supports two modes:
 * 1. NORMAL MODE (previewMode=false): Fetches data from backend API, rendered in iframe via popups.js
 * 2. PREVIEW MODE (previewMode=true): Uses provided previewData for real-time dashboard preview
 *
 * Close button sends EXIT_OVERLAY_ELEMENT postMessage to parent to dismiss the overlay.
 */

// CON-514 / CON-517 surfaces on this page: the message card, and the header
// icon button (36, surface + 1px edge, 20 glyph in text-secondary).
// CON-520 (Paper 75B-0): the state cards float over the host page, so they take the
// overlay shadow; the icon sits in a 56 disc; title 18/26 semibold, body 14/20 secondary.
const MESSAGE_CARD_CLASS =
  'flex flex-col items-center gap-[var(--cs-space-2)] rounded-[var(--cs-card-radius)] bg-[color:var(--cs-surface-0)] border border-[color:var(--cs-border-control)] shadow-[shadow:var(--cs-modal-shadow)]';
const MESSAGE_DISC_CLASS =
  'mb-[var(--cs-space-3)] flex size-[var(--cs-size-icon-circle)] items-center justify-center rounded-[var(--cs-radius-full)] [&_svg]:size-[var(--cs-icon-xl)] [&_svg]:[stroke-width:var(--cs-icon-stroke)]';
const MESSAGE_TITLE_CLASS =
  'm-0 text-[length:var(--cs-text-lg)] leading-[var(--cs-leading-26)] font-semibold text-[color:var(--cs-text-primary)]';
const MESSAGE_BODY_CLASS =
  'm-0 text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]';
// The 20 glyph is set on the button: the Button base sizes descendant svgs.
const OVERLAY_CLOSE_CLASS = cn(
  buttonVariants({ variant: 'secondary', size: 'icon' }),
  '[&_svg]:size-[var(--cs-icon-lg)]',
);
// Lucide x, stroke 1.75, in the secondary text role (SPEC §C Icons, §E3).
const CloseGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="size-[var(--cs-icon-lg)] text-[color:var(--cs-text-secondary)] [stroke-width:var(--cs-icon-stroke)]">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

function OverlayElement({
  elementID,
  backendUrl: propBackendUrl,
  previewMode = false,
  previewData = null,
  onPreviewSubmit,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!previewMode);
  const [error, setError] = useState(null);
  const [domainBlocked, setDomainBlocked] = useState(false); // true when 403 — don't render anything
  const [legacyFormID, setLegacyFormID] = useState(false); // true when elementID is actually a formID (elements-config returned 404)
  const [revealed, setRevealed] = useState(previewMode); // controls content fade-in after data loads
  const [embedFormReady, setEmbedFormReady] = useState(false); // true when EmbedForm fires onReady (data + brand colors loaded)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // The control that opened the form drawer (the detail page's or the bar's Give), so closing
  // the drawer returns focus to it instead of dropping it on the body. The drawer has no Radix
  // Trigger, so Radix has nothing of its own to restore focus to.
  const drawerOpenerRef = useRef(null);
  const openDrawer = () => {
    drawerOpenerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setIsDrawerOpen(true);
  };
  const restoreFocusOnDrawerClose = (event) => {
    event.preventDefault();
    const active = document.activeElement;
    const focusLost = !active || active === document.body || Boolean(event.currentTarget?.contains?.(active));
    if (!focusLost) return;
    const opener = drawerOpenerRef.current;
    const target = opener?.isConnected && opener !== document.body
      ? opener
      : document.querySelector(`[${OVERLAY_OPENER_ATTRIBUTE}]`);
    target?.focus?.({ preventScroll: true });
  };
  // EMB-012: this drawer stays open under the checkout iframe, whose document
  // the donor is typing in. Without the guard, any re-render here makes the
  // drawer's focus trap call focus() on its container and pull the donor's
  // focus out of checkout. See guardFocusWhileDocumentUnfocused.
  const releaseDrawerFocusGuardRef = useRef(null);
  const drawerContentRef = useCallback((node) => {
    releaseDrawerFocusGuardRef.current?.();
    releaseDrawerFocusGuardRef.current = node ? guardFocusWhileDocumentUnfocused(node) : null;
  }, []);
  // A11-003: the drawer's focus trap steals a Tab when react-select tidies its
  // live region on blur. Same handoff ResponsiveDrawer uses.
  const drawerTabHandoff = useDrawerTabHandoff();
  // A11-003: the control that opened checkout, so closing checkout can return
  // focus to it (see checkoutFocusReturn.js).
  const checkoutOpenerRef = useRef(null);
  const cancelFocusReturnRef = useRef(null);
  useEffect(() => () => cancelFocusReturnRef.current?.(), []);
  // CON-11. vaul's Drawer.Portal falls through to Radix's default of
  // `document.body` when given no `container`, and document.body is outside
  // [data-cs-position] — the attribute CON-42 uses to give this widget the
  // DONOR value set inside the dashboard's own document. Everything in the
  // drawer therefore resolved the HOST's tokens, which stopped being invisible
  // when the donate CTA's border and shadow became token reads instead of
  // literals.
  //
  // Resolved from the mounted root rather than passed in, so it is null in
  // production (no ancestor carries the attribute there) and vaul keeps its own
  // default. Only the previewer's behaviour changes.
  const rootRef = useRef(null);
  // The overlay root is also where the brand roles are written and where the supporters
  // modal portals to, so the modal's Give reads the organisation's colour (CON-520).
  const [portalHost, setPortalHost] = useState(null);
  const rootCallbackRef = useCallback((node) => {
    rootRef.current = node;
    setPortalHost(node);
  }, []);
  // The form pane, focused by the supporters modal's Give on desktop.
  const formPaneRef = useRef(null);
  const focusFormPane = useCallback(() => {
    formPaneRef.current?.focus({ preventScroll: true });
  }, []);
  const [drawerScope, setDrawerScope] = useState(null);
  useEffect(() => {
    setDrawerScope(rootRef.current?.closest('[data-cs-position]') ?? null);
  }, [isDrawerOpen]);
  // The FAQs open as a drawer inside the left pane (FAQPaneDrawer in OverlayDetailPane).
  const [isCheckoutActive, setIsCheckoutActive] = useState(false);

  // In preview mode, use previewData directly
  const effectiveData = previewMode ? previewData : data;
  const effectiveFormType = effectiveData?.formType || effectiveData?.elementType;
  const effectiveFormId = effectiveData?.formID || null;
  const accessibleTitle = effectiveFormType === 'EVENT' ? 'Event Registration Form' : 'Donation Form';
  // The mobile drawer's visible title strip names the intent, as the hosted drawer does
  // (Paper 6XZ-0 / 5QU-0, CON-571): "Make a gift", "Get tickets" for an event. Display text
  // only — `accessibleTitle` above stays the iframe title the host script is sent
  // (UPDATE_OVERLAY_FRAME_TITLE, whose contract allows only those two strings).
  const drawerTitle = effectiveFormType === 'EVENT' ? 'Get tickets' : 'Make a gift';

  // Keep the host's loading fallback until we know the form type. The host
  // owns the iframe, but wording stays beside the dialog it names.
  useEffect(() => {
    if (previewMode || !effectiveFormType) return;
    safePostMessageToParent(
      { action: 'UPDATE_OVERLAY_FRAME_TITLE', entity: 'CHARITYSTACK', title: accessibleTitle },
      '*',
    );
  }, [previewMode, effectiveFormType, accessibleTitle]);

  // Check if form is deactivated (element config has active: false).
  // In overlay flow, popups.js redirects before iframe loads, but this is a safety net
  // for preview mode or if the redirect hasn't been deployed yet.
  const isFormDeactivated = !previewMode && !legacyFormID && effectiveData?.active === false;

  // Derive brand color from data
  // CON-528: an organisation with no colour paints the no-brand set (SPEC §A6), never a
  // stand-in sage. The legacy "no colour" value resolves to the neutral palette, and the
  // root provider below writes no brand at all, so every role takes its fallback.
  const configuredFormColor =
    effectiveData?.lightColor || effectiveData?.darkColor || effectiveData?.color || null;
  const formColor = configuredFormColor || LEGACY_UNBRANDED_FORM_COLOR;
  const { brandColors: generatedBrandColors } = useBrandColors(formColor);
  const brandColorHex = effectiveData?.lightColor || effectiveData?.color || null;

  // Fetch form config from backend (skip in preview mode)
  useEffect(() => {
    if (previewMode) return;
    let currentRequest = true;

    const fetchConfig = async () => {
      if (!elementID) {
        setError('No elementID provided');
        setLoading(false);
        return;
      }

      const backendUrl = propBackendUrl || getBackendUrl();
      if (!backendUrl) {
        console.error('OverlayElement: Backend URL is not configured');
        setError('Backend URL not configured');
        setLoading(false);
        return;
      }

      try {
        // Pass overlayDomain if present in URL params (set by popups.js) for backend domain verification.
        // popups.js also checks domain before creating the iframe, but this is a safety net.
        const urlParams = new URLSearchParams(window.location.search);
        const overlayDomain = urlParams.get('overlayDomain');
        const response = await fetch(
          buildGroupTicketV2ElementsConfigUrl({ backendUrl, elementID, overlayDomain }),
        );
        if (!currentRequest) return;

        if (response.status === 404) {
          // elementID is likely a legacy formID (from ?donate&formid= URLs).
          // Switch to legacy mode — EmbedForm will fetch its own config using this ID.
          console.log('OverlayElement: Element not found, switching to legacy formID mode:', elementID);
          setLegacyFormID(true);
          setLoading(false);
          return;
        }

        if (response.status === 403) {
          // Domain not authorized — tell parent to close overlay and clean the URL
          console.log('OverlayElement: Domain not authorized, closing overlay');
          safePostMessageToParent(
            { action: 'EXIT_OVERLAY_ELEMENT', entity: 'CHARITYSTACK' },
            '*',
          );
          setDomainBlocked(true);
          setLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }

        const fetchedData = await response.json();
        if (!currentRequest) return;
        setData(fetchedData);
      } catch (err) {
        if (!currentRequest) return;
        if (isDevelopment()) {
          logApiError('OverlayElement', err, { elementID });
        }
        console.error('OverlayElement: Failed to fetch config:', err);
        setError(err.message);
      } finally {
        if (currentRequest) setLoading(false);
      }
    };

    fetchConfig();
    // Superseded requests must not overwrite config (or retitle the host),
    // including when fetch has completed but its JSON body is still pending.
    return () => { currentRequest = false; };
  }, [elementID, propBackendUrl, previewMode]);

  // Step 1: When EmbedForm is ready (or error), snap content to visible (opacity: 1).
  // Do NOT signal the parent yet — React must re-render and the browser must paint first.
  useEffect(() => {
    if (previewMode) return;
    if (error || isFormDeactivated) {
      setRevealed(true);
    }
  }, [error, isFormDeactivated, previewMode]);

  useEffect(() => {
    if (previewMode) return;
    if (embedFormReady) {
      requestAnimationFrame(() => {
        setRevealed(true);
      });
    }
  }, [embedFormReady, previewMode]);

  // Step 2: After revealed=true is committed and painted, signal parent (popups.js)
  // to start the iframe scale-in transition. This two-step approach prevents the Chrome
  // glitch where popups.js starts the iframe opacity transition before React has painted
  // the brand-colored content at opacity: 1, causing a brief flash of default colors.
  useEffect(() => {
    if (previewMode) return;
    if (revealed) {
      // Double-rAF ensures the browser has committed the paint with opacity: 1
      // before we tell popups.js to reveal the iframe.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          safePostMessageToParent(
            { action: 'OVERLAY_ELEMENT_READY', entity: 'CHARITYSTACK' },
            '*',
          );
        });
      });
    }
  }, [revealed, previewMode]);

  // Track overlay opened analytics (skip in preview mode)
  useEffect(() => {
    if (previewMode) return;
    if (data && elementID) {
      trackForm(FORM_EVENTS.OVERLAY_OPENED, {
        elementID,
        formType: data.formType || data.elementType,
        form_id: data.formID || null,
        overlay_entry: 'donation_elements_overlay',
      });
    }
  }, [data, elementID, previewMode]);

  // Close overlay — send postMessage to parent (popups.js)
  // In preview mode, this is a no-op since there's no parent iframe.
  // closeReason distinguishes programmatic closes ('cart_add') from user-initiated
  // ones ('user_action') so analytics can separate the two flows.
  // closeReason may be a string ('cart_add', 'user_action') or a SyntheticEvent
  // when handleClose is passed directly as an onClick/onMouseDown handler. Narrow
  // to string so event objects from React don't pollute the analytics property.
  const handleClose = useCallback((closeReason) => {
    const reason = typeof closeReason === 'string' ? closeReason : 'user_action';
    if (previewMode) return;
    trackForm(FORM_EVENTS.OVERLAY_CLOSED, {
      elementID,
      formType: effectiveFormType,
      form_id: effectiveFormId,
      overlay_entry: 'donation_elements_overlay',
      checkout_active: isCheckoutActive,
      close_reason: reason,
    });
    safePostMessageToParent(
      {
        action: 'EXIT_OVERLAY_ELEMENT',
        entity: 'CHARITYSTACK',
      },
      '*',
    );
  }, [previewMode, elementID, effectiveFormType, effectiveFormId, isCheckoutActive]);

  // Listen for ESC key to close (skip in preview mode and when checkout is active)
  useEffect(() => {
    if (previewMode || isCheckoutActive) return;
    // A11-003: an Escape an open popup takes (a multi-select menu) must not
    // close the overlay. Judged in capture, before the popup closes itself.
    let escapeOwnedByPopup = false;
    // CON-520: an Escape inside the supporters modal closes the modal, not the overlay.
    // (The vaul form drawer keeps today's behaviour, so it is excluded.)
    const notePopupEscape = (e) => {
      escapeOwnedByPopup =
        escapeBelongsToOpenPopup(e) ||
        Boolean(e.target?.closest?.('[role="dialog"]:not([data-vaul-drawer])'));
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !escapeOwnedByPopup) {
        handleClose();
      }
      escapeOwnedByPopup = false;
    };
    window.addEventListener('keydown', notePopupEscape, true);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', notePopupEscape, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, previewMode, isCheckoutActive]);

  // Hide close button when checkout is triggered (prevents the X from showing
  // behind the checkout iframe on desktop overlay). The custom event is fired
  // by scheduleFormReset (formResetUtils.js) which all form types call after
  // sending the DONATE postMessage.
  useEffect(() => {
    if (previewMode) return;

    const onCheckoutTriggered = () => {
      // A reopen inside the last close's focus-return window ends that return.
      cancelFocusReturnRef.current?.();
      cancelFocusReturnRef.current = null;
      const active = document.activeElement;
      checkoutOpenerRef.current = active && active !== document.body ? active : null;
      setIsCheckoutActive(true);
    };
    window.addEventListener('charitystack:checkout-triggered', onCheckoutTriggered);

    // Also listen for postMessages from the parent — if the checkout exits
    // (user clicks back), the parent may forward EXIT_CHECKOUT_FORM so the
    // overlay can restore the close button.
    const onMessage = (event) => {
      if (event.data?.entity !== 'CHARITYSTACK') return;
      if (
        event.data.action === 'EXIT_CHECKOUT_FORM' ||
        event.data.action === 'DONATION_PROCESSED'
      ) {
        setIsCheckoutActive(false);
      }
      if (event.data.action === 'EXIT_CHECKOUT_FORM') {
        const opener = checkoutOpenerRef.current;
        checkoutOpenerRef.current = null;
        cancelFocusReturnRef.current?.();
        cancelFocusReturnRef.current = scheduleFocusReturnAfterCheckout(opener, document);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('charitystack:checkout-triggered', onCheckoutTriggered);
      window.removeEventListener('message', onMessage);
    };
  }, [previewMode]);

  // Close overlay when the embedded form successfully fires UPDATE_CART.
  // The external hosted script (insert-elements.js) will open the cart-sheet
  // iframe in response; the overlay's job is done and we don't want it
  // lingering behind the cart. Mirrors the same-window signaling pattern used
  // for checkout above (charitystack:checkout-triggered).
  // Guard on isCheckoutActive to match the ESC listener (line 204) — keeps the
  // two auto-close paths symmetric even though they're mutually exclusive today.
  useEffect(() => {
    if (previewMode || isCheckoutActive) return;
    const onCartAddTriggered = () => handleClose('cart_add');
    window.addEventListener('charitystack:cart-add-triggered', onCartAddTriggered);
    return () => {
      window.removeEventListener('charitystack:cart-add-triggered', onCartAddTriggered);
    };
  }, [previewMode, isCheckoutActive, handleClose]);

  // Still loading — the two-pane skeleton (owner call 2026-10-23). The host
  // (popups.js) keeps this iframe at opacity 0 behind its own spinner until
  // OVERLAY_ELEMENT_READY or its 3s fallback, so a donor sees this frame only
  // when the fallback reveals a slow load — where it used to see an empty,
  // transparent frame. Showing it from the first paint would need a host
  // change; the handshake is untouched here.
  if (!previewMode && loading) {
    return (
      <OverlaySkeleton
        closeButton={
          <button onClick={handleClose} className={OVERLAY_CLOSE_CLASS} aria-label="Close overlay">
            <CloseGlyph />
          </button>
        }
      />
    );
  }

  // Domain not authorized (403) — render nothing, overlay simply doesn't appear
  if (domainBlocked) {
    return null;
  }

  if (!previewMode && !legacyFormID && (error || !data)) {
    return (
      <div
        className="flex items-center justify-center min-h-screen-safe bg-transparent"
        style={{
          opacity: revealed ? 1 : 0,
        }}
      >
        <div className={`text-center p-8 max-w-sm w-full mx-4 ${MESSAGE_CARD_CLASS}`}>
          <div className={`${MESSAGE_DISC_CLASS} bg-[color:var(--cs-status-error-bg)] text-[color:var(--cs-status-error-text)]`}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
          </div>
          <p className={MESSAGE_TITLE_CLASS}>Unable to load form</p>
          {/* Paper 75B-0: the reason that used to hide in an info-icon tooltip is plain text. */}
          <p className={MESSAGE_BODY_CLASS}>This form isn&apos;t configured to render on this domain.</p>
          <button
            onClick={handleClose}
            className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'mt-[var(--cs-space-3)]')}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Deactivated form — show a centered message instead of the two-pane layout
  if (isFormDeactivated) {
    return (
      <div
        className={`overlay-element-container flex flex-col items-center justify-center ${previewMode ? 'min-h-full' : 'min-h-screen-safe'}`}
        style={!previewMode ? { opacity: revealed ? 1 : 0 } : undefined}
      >
        {!previewMode && (
          <button
            onClick={handleClose}
            className={cn(OVERLAY_CLOSE_CLASS, 'fixed top-3 right-3 z-50')}
            aria-label="Close overlay"
          >
            <CloseGlyph />
          </button>
        )}
        <div className={`text-center p-8 max-w-sm w-full mx-4 ${MESSAGE_CARD_CLASS}`}>
          <div className={`${MESSAGE_DISC_CLASS} bg-[color:var(--cs-surface-1)] text-[color:var(--cs-text-secondary)]`}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="m4.9 4.9 14.2 14.2" />
            </svg>
          </div>
          <p className={MESSAGE_TITLE_CLASS}>This form is currently inactive</p>
          <p className={MESSAGE_BODY_CLASS}>
            {effectiveData?.formName || 'This form'} is not accepting submissions at this time.
          </p>
          {!previewMode && (
            <button
              onClick={handleClose}
              className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'mt-[var(--cs-space-3)]')}
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  const isEventForm = effectiveFormType === 'EVENT';
  const paymentsOff = !previewMode && effectiveData?.paymentsEnabled === false;

  // CON-750: the detail pane's bar and donor list sit outside the form's provider
  // (the form is drawn twice, card and drawer); they show what the form publishes.
  return (
    <FollowDisplayCurrency>
    <div
      ref={rootCallbackRef}
      className={`overlay-element-container flex flex-col ${previewMode ? 'min-h-full' : 'min-h-screen-safe'}`}
      style={
        !previewMode
          ? {
              opacity: revealed ? 1 : 0,
              // No CSS transition — snap instantly. The outer iframe transition
              // in popups.js (scale + opacity 0.35s) handles the smooth animation.
              // A double-fade here would expose intermediate renders (default colors)
              // during the early frames of the transition.
            }
          : undefined
      }
    >
      {/* CON-520: the brand roles are written on the overlay root, so the left pane, the
          mobile bar and the supporters modal (portalled here) all read the organisation's
          colour. The form panes still mount their own provider (see below). */}
      <BrandColorProvider
        brandColors={configuredFormColor ? generatedBrandColors : undefined}
        scope={rootRef}
      >
      {/* Desktop Layout — side-by-side.
          Outer div is the click-outside-to-close zone (full viewport centering container).
          Clicking outside the frosted card triggers handleClose.

          CON-8, on the three viewport-height reads below (this wrapper's min-height and the
          max-height on both panes): they are deliberately NOT the `.min-h-screen-safe` /
          `.h-screen-safe` utilities the rest of this file uses, and they read vh rather than dvh.

          Two reasons, in order. First, this whole subtree renders only at the desktop fork and
          above — the wrapper is display:none below it — so the collapsing mobile toolbar those
          units exist for does not arise here, and vh is simply the right answer. Second, the safe
          utilities are hand-written classes with no responsive variant, and the fallback they
          carry lives in an `@supports` block (see the note in shared-styles/utilities.css for the
          minifier behaviour that forced that shape) — which a Tailwind arbitrary value or the
          built-in dvh utility cannot express at all. Getting that wrong is not cosmetic: a
          single dvh declaration is dropped whole by a browser that cannot parse it, and two of
          these three are the panes' max-height clamp, so the right pane's `overflow-y-auto` would
          stop scrolling and the submit button could land off-screen.

          If the structural fork ever moves down (CON-8's open decision), these three move with it
          and will then need the toolbar-safe pair — which means giving the utilities a
          breakpoint-scoped form, not switching these to a bare dvh.

          Naming the utilities in prose rather than as class tokens is deliberate too: Tailwind v4
          scans this file as raw text, so a class name written inside a comment is enough to emit
          the rule into four shipped stylesheets. The first draft of this comment did exactly
          that. */}

      <div
        className={`hidden w-full lg:flex lg:items-center lg:justify-center ${previewMode ? '' : 'lg:min-h-screen lg:py-10'}`}
        onMouseDown={!previewMode && !isCheckoutActive ? handleClose : undefined}
        role="presentation"
      >
        {/* CON-520 (Paper 7CM-0 / S8M-0): the shell — card radius on the sunken overlay
            ground, 1px edge, overlay shadow, 8 padding and gap — holding two equal-height
            white panes. `overlay-mode` keeps the form's overlay type sizes (forms.css). */}
        <div
          className="relative w-fit lg:flex lg:items-stretch lg:gap-[var(--cs-space-2)] lg:justify-center overlay-mode rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-overlay-surface)] p-[var(--cs-space-2)] shadow-[shadow:var(--cs-modal-shadow)]"
          onMouseDown={(e) => e.stopPropagation()} /* Clicks inside the card don't close */
          role="presentation"
          style={{
            minHeight: data?.formType === 'EMBED_FORM'
              ? OVERLAY_CARD_STANDARD_MIN_HEIGHT
              : OVERLAY_CARD_MIN_HEIGHT,
          }}
        >
          {/* Close — the header icon button, 8 to the right of the shell's top corner
              (Paper 7CM-0). Hidden in preview mode and while checkout is active (it would
              show behind the checkout iframe). */}
          {!previewMode && !isCheckoutActive && (
            <button
              onClick={handleClose}
              className={cn(OVERLAY_CLOSE_CLASS, 'absolute left-full top-0 z-50 ml-[var(--cs-space-2)]')}
              aria-label="Close overlay"
            >
              <CloseGlyph />
            </button>
          )}

          {/* Left pane — the campaign. It scrolls on its own when its content is taller than
              the shell; the FAQs drawer slides up inside it. */}
          <div
            className={`relative w-full lg:w-[28rem] max-w-md flex-none flex flex-col min-h-0 overflow-hidden rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-0)] ${previewMode ? '' : 'max-h-[calc(100vh-var(--cs-space-10)*2-var(--cs-space-2)*2)]'}`}
          >
            <OverlayDetailPane
              data={effectiveData}
              brandColors={generatedBrandColors}
              previewMode={previewMode}
              portalContainer={portalHost}
              onGive={focusFormPane}
            />
          </div>

          {/* Right Pane — Form */}
          <div
            ref={formPaneRef}
            tabIndex={-1}
            className={`w-full lg:w-[28rem] max-w-md flex-none overflow-y-auto overlay-form-stretch rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-0)] focus:outline-none ${previewMode ? '' : 'max-h-[calc(100vh-var(--cs-space-10)*2-var(--cs-space-2)*2)]'}`}
          >
              {/* The brand provider is mounted HERE for the same reason
                  App.jsx mounts it on the EMBED_FORM entity: the donor controls
                  inside read brand through scoped custom properties, not only
                  through props, and a surface without the provider silently
                  renders the unbranded fallback of every one of them. Measured
                  when CON-157 added the hover ground to the provider's write:
                  applying the whole write to this container changes 0 of 305
                  elements' painted styles at rest, so this restores the brand
                  read the hover ground needs and repaints nothing else. */}
            <EmbedForm
              formID={elementID}
              isDrawerOpen={false}
              brandColors={brandColorHex}
              backendUrl={propBackendUrl}
              mountBrandProvider
              previewMode={previewMode}
              previewData={previewMode ? previewData : effectiveData}
              onPreviewSubmit={onPreviewSubmit}
              formDisplayMode="overlay"
              onReady={() => setEmbedFormReady(true)}
              elementPrefills={effectiveData?.prefillParams || null}
            />
          </div>
        </div>
      </div>

      {/* Mobile Layout — mirrors the hosted page's mobile scroll (CON-520, Paper 6PO-0 /
          728-0): the 56 header row (28 logo tile left, Close right), then the campaign. */}
      {/* data-pane-sheet-scope: the screen-height frame the About / FAQs sheet covers. */}
      <div className="block lg:hidden h-screen-safe overflow-hidden bg-[color:var(--cs-surface-1)]" data-pane-sheet-scope="">
        <div
          className="h-full overflow-y-auto px-4 pt-1 pb-24"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex h-[var(--cs-size-header)] items-center justify-between gap-[var(--cs-space-2)]">
            <OrgLogoTile
              merchantName={effectiveData?.nonprofitName || ''}
              logo={effectiveData?.logo}
              logoVersion={effectiveData?.logoVersion}
              s3LogoUrl={import.meta.env.VITE_S3_LOGO_URL}
              size="md"
            />
            {!previewMode && !isCheckoutActive && (
              <button onClick={handleClose} className={OVERLAY_CLOSE_CLASS} aria-label="Close overlay">
                <CloseGlyph />
              </button>
            )}
          </div>
          <div className="pt-[var(--cs-space-3)]">
            {/* CampaignLeftPanel — maps overlay effectiveData fields to hosted-page pageData prop names. */}
            <CampaignLeftPanel
              formName={effectiveData?.formName || ''}
              organizedBy={effectiveData?.organizedBy || effectiveData?.nonprofitName || ''}
              merchantName={effectiveData?.nonprofitName || ''}
              formID={effectiveData?.formID || effectiveData?.elementID || ''}
              formType={effectiveData?.formType || effectiveData?.elementType || ''}
              headerImage={effectiveData?.image || effectiveData?.headerImage || ''}
              imageVersion={effectiveData?.imageVersion}
              logo={effectiveData?.logo}
              logoVersion={effectiveData?.logoVersion}
              brandColors={generatedBrandColors}
              previewMode={previewMode}
              showFundraisingBar={effectiveData?.showFundraisingBar || false}
              fundraisingBarID={effectiveData?.fundraisingBarElementID || null}
              raisedAmount={
                (parseInt(effectiveData?.totalTransactionAmount) || 0) +
                (parseInt(effectiveData?.amountOffset) || 0)
              }
              targetAmount={effectiveData?.targetAmount || 0}
              trackingMetric={effectiveData?.trackingMetric}
              metricLabel={effectiveData?.metricLabel}
              elementOffset={effectiveData?.elementOffset || 0}
              fundraiserDetails={effectiveData?.details || effectiveData?.eventDetails || ''}
              formFaqs={effectiveData?.formFaqs || effectiveData?.customFAQs || []}
              enableDonorList={effectiveData?.enableDonorList || false}
              donorListID={effectiveData?.donorListElementID || null}
              donorListType={effectiveData?.donorListType || 'RECENT_DONORS'}
              donorListData={effectiveData?.donorListData || null}
              showAllSupportersAnonymous={effectiveData?.showAllSupportersAnonymous || false}
              onGive={openDrawer}
              portalContainer={portalHost}
              enableTimeAndLocation={effectiveData?.enableTimeAndLocation}
              eventStartDate={effectiveData?.eventStartDate || effectiveData?.eventDate || null}
              eventEndDate={effectiveData?.eventEndDate || null}
              eventStartTime={effectiveData?.eventStartTime || null}
              eventEndTime={effectiveData?.eventEndTime || null}
              eventLocation={effectiveData?.eventLocation || null}
              timeZone={effectiveData?.timeZone || null}
            />
          </div>
        </div>

        {/* Drawer with EmbedForm — direct vaul usage (no shadcn wrapper) to avoid auto-overlay/scaling issues inside iframe.
            IMPORTANT: Do NOT use drawer-frosted-overlay (backdrop-filter) on Drawer.Overlay here.
            The parent page (popups.js parentDiv) already applies charitystack-frosted-overlay with backdrop-filter.
            Nesting two backdrop-filter layers through an iframe boundary causes compositing artifacts
            (extra frames, content shrinking, flickering) — the same bug that was fixed in popups.js. */}
        <Drawer.Root open={isDrawerOpen} onOpenChange={setIsDrawerOpen} noBodyStyles>
          <Drawer.Portal container={drawerScope ?? undefined}>
            <Drawer.Overlay
              className="fixed inset-0 z-[50] bg-[color:var(--cs-drawer-backdrop)]"
              style={{ height: '100dvh' }}
            />
            {/* Top radius and max height both read the drawer dials, so this
                sheet matches every other donor drawer (CON-20 AC6, design.md
                row D). */}
            <Drawer.Content
              ref={drawerContentRef}
              // Titled, with no description: say so (silences Radix's "Missing Description" warning).
              aria-describedby={undefined}
              {...drawerTabHandoff}
              onCloseAutoFocus={restoreFocusOnDrawerClose}
              // A11-003: an Escape that closes an open list belongs to the list,
              // not to the drawer (same rule as the overlay's own Escape close).
              onEscapeKeyDown={(event) => {
                if (escapeBelongsToOpenPopup(event)) event.preventDefault();
              }}
              className="drawer-content fixed inset-x-0 bottom-0 flex flex-col bg-[color:var(--cs-surface-0)] rounded-t-[var(--cs-drawer-radius-top)] shadow-[shadow:var(--cs-sheet-shadow)] z-[51] h-fit max-h-[var(--cs-drawer-max-height)]">
              {/* CON-517 (Paper 5QU-0): the 52 title strip — title, grabber,
                  transparent 36 close. */}
              <div className="relative flex flex-shrink-0 items-center justify-between gap-3 px-5 h-[var(--cs-size-title-strip)] rounded-t-[var(--cs-drawer-radius-top)] bg-[color:var(--cs-surface-1)] border-b border-[color:var(--cs-border-control-subtle)]">
                <div className="absolute inset-x-0 top-2 mx-auto w-[var(--cs-drawer-handle-width)] h-[var(--cs-drawer-handle-height)] rounded-full bg-[color:var(--cs-border-control-hover)]" />
                <Drawer.Drawer.Title className="text-[length:var(--cs-title-strip-font-size)] leading-[var(--cs-title-strip-line-height)] font-semibold text-[color:var(--cs-text-primary)] truncate">
                  {drawerTitle}
                </Drawer.Drawer.Title>
                <Drawer.Drawer.Close asChild>
                  <button
                    type="button"
                    aria-label="Close drawer"
                    className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), '[&_svg]:size-[var(--cs-icon-lg)]')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="[stroke-width:var(--cs-icon-stroke)]" aria-hidden="true">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </Drawer.Drawer.Close>
              </div>
              <div className="mobile-drawer-content px-2 sm:px-4 pb-2">
                {/* See the note on the desktop pane above. */}
                <EmbedForm
                  formID={elementID}
                  isDrawerOpen={isDrawerOpen}
                  brandColors={brandColorHex}
                  backendUrl={propBackendUrl}
                  mountBrandProvider
                  previewMode={previewMode}
                  previewData={previewMode ? previewData : effectiveData}
                  onPreviewSubmit={onPreviewSubmit}
                  formDisplayMode="dynamic"
                  onReady={() => setEmbedFormReady(true)}
                  elementPrefills={effectiveData?.prefillParams || null}
                  hideFAQ
                />
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>

        {/* The sticky Give bar (Paper 6PO-0): 12 / 16 / 20 padding (plus the safe area),
            the primary 48 button with a 15/22 label and no icon. It slides away while the form
            drawer or an About / FAQs sheet (data-pane-sheet-open on the frame) is open. */}
        <div
          className={`
            fixed bottom-0 left-0 right-0 z-[52]
            transition-all duration-[var(--cs-motion-base)] ease-in-out
            transform-gpu
            block lg:hidden
            ${isDrawerOpen ? 'translate-y-full pointer-events-none' : 'translate-y-0 pointer-events-auto'}
            [[data-pane-sheet-open]_&]:translate-y-full [[data-pane-sheet-open]_&]:pointer-events-none
          `}
        >
          <div
            className="w-full px-4 pt-3 bg-[color:var(--cs-surface-0)] border-t border-[color:var(--cs-border-control-subtle)]"
            style={{ paddingBottom: 'calc(var(--cs-space-5) + env(safe-area-inset-bottom, 0px))' }}
          >
            {paymentsOff && (
              <p role="status" className="mb-2 text-center text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]">
                Payments are temporarily unavailable. Please try again later.
              </p>
            )}
            <button
              className={cn(buttonVariants({ family: 'cta', variant: 'cta', size: 'cta' }), 'relative')}
              disabled={paymentsOff && !isEventForm}
              onClick={openDrawer}
              {...{ [OVERLAY_OPENER_ATTRIBUTE]: '' }}
              type="button"
            >
              {isEventForm ? 'Get tickets' : 'Give'}
            </button>
          </div>
        </div>
      </div>
      </BrandColorProvider>
    </div>
    </FollowDisplayCurrency>
  );
}

export default OverlayElement;
